import axios from 'axios';
import { Invoice, GLAccount, Job, GLLineItem, HeaderEdits, CreditCard } from './types';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

export const getInvoice = async (identifier: string): Promise<Invoice> => {
  // If it looks like a tracking ID (starts with INV-), use tracking_id param (internal access).
  // Otherwise use token param (external email link access).
  const isTrackingId = identifier.length < 30;
  const response = await axios.get(`${API_BASE}/invoice`, {
    params: isTrackingId ? { tracking_id: identifier } : { token: identifier }
  });
  return response.data;
};

export const submitCorrection = async (
  identifier: string,
  correctedLines: GLLineItem[],
  correctionNotes: string,
  action: 'CORRECTED' | 'APPROVED' | 'REJECTED',
  headerEdits: HeaderEdits
): Promise<void> => {
  const isTrackingId = identifier.length < 30;
  await axios.post(`${API_BASE}/invoice`, {
    corrected_gl_line_items: correctedLines,
    correction_notes: correctionNotes,
    action,
    invoice_amount: headerEdits.invoice_amount,
    invoice_date: headerEdits.invoice_date,
    invoice_number: headerEdits.invoice_number,
    payment_method_id: headerEdits.payment_method_id,
    // Only sent when Credit Card; otherwise null clears the field in the DB
    credit_card_vendor_number: headerEdits.payment_method_id === 2
      ? headerEdits.credit_card_vendor_number
      : null,
    posting_date: headerEdits.posting_date,
    // Vendor fields — only relevant for OTHER invoices where AI couldn't identify vendor
    vendor_number: headerEdits.vendor_number ?? null,
    vendor_name: headerEdits.vendor_name || null,
  }, {
    params: isTrackingId ? { tracking_id: identifier } : { token: identifier }
  });
};

export const getGLAccounts = async (): Promise<GLAccount[]> => {
  const response = await axios.get(`${API_BASE}/gl-accounts`);
  return response.data;
};

export const getJobs = async (): Promise<Job[]> => {
  const response = await axios.get(`${API_BASE}/jobs`);
  return response.data;
};

export const getCreditCards = async (): Promise<CreditCard[]> => {
  const response = await axios.get(`${API_BASE}/credit-cards`);
  return response.data;
};

export const setInvoiceStatus = async (
  trackingIds: string[],
  status: string,
  reason?: string
): Promise<{ updated_count: number; results: any[] }> => {
  const response = await axios.post(`${API_BASE}/invoices/set-status`, {
    tracking_ids: trackingIds,
    status,
    reason: reason || '',
  });
  return response.data;
};

export const getWinTeamEnvironment = async (): Promise<{ environment: string; is_production: boolean; db_name: string }> => {
  const response = await axios.get(`${API_BASE}/environment`);
  return response.data;
};

export interface InvoiceAttachment {
  filename: string;
  content_type: string;
  url: string;
  has_preview: boolean;
  preview_url?: string;
}

export const getInvoiceAttachment = async (identifier: string): Promise<InvoiceAttachment> => {
  const isTrackingId = identifier.length < 30;
  const response = await axios.get(`${API_BASE}/invoice/attachment`, {
    params: isTrackingId ? { tracking_id: identifier } : { token: identifier }
  });
  return response.data;
};

export interface VendorSearchResult {
  vendor_number: number;
  vendor_name: string;
}

export const searchVendors = async (q: string): Promise<VendorSearchResult[]> => {
  if (!q.trim()) return [];
  const response = await axios.get(`${API_BASE}/vendors/search`, { params: { q } });
  return response.data;
};

export interface BrandingSettings {
  portal_logo_url: string;
  reverse_logo_url: string;
  portal_tab: string;
}

export const getBranding = async (): Promise<BrandingSettings> => {
  try {
    const response = await axios.get(`${API_BASE}/branding`);
    return response.data;
  } catch {
    // Fail open — branding errors must never block the portal
    return { portal_logo_url: '', reverse_logo_url: '', portal_tab: '' };
  }
};